import React from "react";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";

const sections = [
  {
    title: "Creativity",
    text: `Codex Future Technologies has proven past experience, in terms of creativity and quality.
           It provides unique web designs and themes for their clients as per their requirements
           and budget to make every website stand out from the crowd.`,
    img: "../../ecom1.jpg",
  },
  {
    title: "Responsive",
    text: `Our responsive designs adapt seamlessly across devices. Whether it’s a laptop,
           tablet, or smartphone, your website looks perfect everywhere.`,
    img: "../../ecom2.jpg",
    reverse: true,
  },
  {
    title: "Dynamic",
    text: `Modern websites are optimized for all devices and feature intuitive designs with
           dynamic content and smart interactive elements.`,
    img: "../../ecom3.jpg",
  },
  {
    title: "Customised",
    text: `We craft custom websites tailored to your needs, ensuring maximum usability and
           an attractive UI that supports your business goals.`,
    img: "../../ecom4.jpg",
    reverse: true,
  },
];

const ScrollSection = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 60 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.7, ease: "easeOut" }}
    viewport={{ once: false, amount: 0.3 }}
  >
    {children}
  </motion.div>
);

export default function EcommerceSolutionsTailwind() {
  const handleScroll = () => {
    document.getElementById("ecommerce-sections").scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="font-poppins text-gray-900 overflow-x-hidden scroll-smooth relative">

      {/* Background */}
      <div
        className="fixed inset-0 -z-10 bg-cover bg-center bg-fixed"
        style={{ backgroundImage: `url('../../eco.jpg')` }}
      >
        <div className="absolute inset-0 bg-black/60" />
      </div>

      {/* HERO */}
      <ScrollSection>
        <section className="relative h-[90vh] sm:h-screen flex flex-col items-center justify-center text-center px-4">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold uppercase tracking-wider mb-5 text-white drop-shadow-lg"
          >
            ECommerce Solutions
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="text-base sm:text-lg md:text-xl max-w-lg sm:max-w-2xl text-gray-300 leading-relaxed"
          >
            Helping your business thrive online with scalable and user-friendly ecommerce platforms.
          </motion.p>

          {/* Centered Arrow */}
          <motion.div
            className="absolute bottom-10 sm:bottom-20 left-1/2 -translate-x-1/2 cursor-pointer animate-bounce text-red-600"
            onClick={handleScroll}
            whileHover={{ scale: 1.2 }}
          >
            <ChevronDown size={36} className="sm:size-[42px]" />
          </motion.div>
        </section>
      </ScrollSection>

      {/* SECTIONS */}
      <div
        id="ecommerce-sections"
        className="max-w-6xl mx-auto py-16 sm:py-20 md:py-24 px-4 sm:px-6 md:px-10 space-y-20 sm:space-y-24 relative z-10"
      >
        {sections.map(({ title, text, img, reverse }, i) => (
          <ScrollSection key={i}>
            <div
              className={`flex flex-col md:flex-row items-center gap-10 sm:gap-12 ${
                reverse ? "md:flex-row-reverse" : ""
              }`}
            >
              {/* TEXT */}
              <motion.div
                className="flex-1 bg-white/90 backdrop-blur-[14px] p-6 sm:p-8 md:p-10 rounded-[20px] shadow-[0_10px_25px_rgba(209,17,17,0.2)] hover:shadow-[0_14px_35px_rgba(209,17,17,0.35)] transition-all duration-300"
                whileHover={{ y: -5 }}
              >
                <h2 className="text-2xl sm:text-3xl md:text-[38px] font-bold mb-4 relative after:block after:w-20 after:h-1 after:bg-red-600 after:rounded mt-2">
                  {title}
                </h2>

                <p className="text-sm sm:text-base md:text-[17px] leading-relaxed text-gray-700">
                  {text}
                </p>
              </motion.div>

              {/* IMAGE */}
              <motion.div className="flex-1 text-center" whileHover={{ scale: 1.05 }}>
                <img
                  src={img}
                  className="w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg rounded-[20px] shadow-[0_12px_30px_rgba(209,17,17,0.2)]"
                  alt={title}
                />
              </motion.div>
            </div>
          </ScrollSection>
        ))}
      </div>

      <style>
        {`
          @keyframes bounce {
            0%,100% { transform: translateY(0); }
            50% { transform: translateY(10px); }
          }
          .animate-bounce {
            animation: bounce 1.8s infinite;
          }
        `}
      </style>
    </div>
  );
}
